import retryCallIfNeeded from '../../../restCalls';

const basePath = window.__ENV.REACT_APP_BASE_PATH || '/application';
const baseEndpoint = '/api/applications/v1';
const baseRulesEndpoint = '/api/applications/rules/v1';
const applicationProxy = `${basePath}/new/app`;
const navigationProxy = `${basePath}/new/nav`;
const ruleEngineProxy = `${basePath}/new/a`;
const dvRejectRuleProxy = `${basePath}/new/r`;

export default function ApplicationService(client) {
  const applicationTypeDetailsRequests = {};
  const getApplicationByPersonIdAsync = personId => retryCallIfNeeded(client, () => client
    .get(`${baseEndpoint}/applications?index=personId&value=${personId}`));
  const getApplicationByApplicationIdAsync = applicationId => retryCallIfNeeded(client, () => client
    .get(`${baseEndpoint}/applications/${applicationId}`));
  const postApplicationAsync = payload => retryCallIfNeeded(client, () => client
    .post(`${baseEndpoint}/applications`, payload));
  const updateApplicationAsync = async (application, applicationETag) => {
    const url = `${baseEndpoint}/applications/${application.id}`;
    const config = {
      headers: {
        'If-Match': applicationETag,
      },
    };
    return client.put(url, application, config);
  };
  const patchApplicationAsync = async (applicationId, payload) => {
    const url = `${baseEndpoint}/applications/${applicationId}`;
    return client.patch(url, payload);
  };
  const getNavigationByApplicationIdAsync = applicationId => retryCallIfNeeded(client, () => client
    .get(`${baseEndpoint}/applications/${applicationId}/navigation`));
  const postNavigationAsync = (applicationId, payload) => retryCallIfNeeded(client, () => client
    .post(`${baseEndpoint}/applications/${applicationId}/navigation`, payload));
  const updateNavigationAsync = async (navigation, applicationId) => {
    const url = `${baseEndpoint}/applications/${applicationId}/navigation`;
    return client.put(url, navigation);
  };
  const getApplicationType = payload => retryCallIfNeeded(client, () => client.post(`${baseRulesEndpoint}/stateless/ruleset/applicationType`, payload));
  const getApplicationTypeDetails = personId => {
    if (!applicationTypeDetailsRequests[personId]) {
      const request = () => retryCallIfNeeded(client, () => client.get(
        `${baseEndpoint}/rules/applicationType?personId=${personId}`,
      ), 0);
      applicationTypeDetailsRequests[personId] = request().catch(error => {
        // This GET is shared by CYOP and University Savings. Recover from one
        // transient gateway failure without replaying authorization or validation errors.
        if ([502, 503, 504].includes(error.response?.status)) return request();
        throw error;
      }).finally(() => {
        delete applicationTypeDetailsRequests[personId];
      });
    }
    return applicationTypeDetailsRequests[personId];
  };
  const putApplywebAppEnrollmentStatusAsync = async (
    applyWebAppId,
    enrollmentStatus,
  ) => retryCallIfNeeded(client, () => client
    .put(`${baseEndpoint}/application/${applyWebAppId}/enrollmentStatus?enrollmentStatusName=${enrollmentStatus}`, null));
  const uploadDocuments = payload => retryCallIfNeeded(client, () => client
    .post(`${baseEndpoint}/applications/uploadDocument`, payload));
  const uploadTranscriptDocument = payload => retryCallIfNeeded(client, () => client
    .post(`${baseEndpoint}/applications/uploadTranscriptDocument`, payload));
  const getProgramWorkExperienceRequirement = payload => retryCallIfNeeded(client, () => client.post(`${baseRulesEndpoint}/stateless/ruleset/programWorkExpRequirment`, payload));
  const getStackableSavingsRuleset = params => retryCallIfNeeded(client, () => client.get(
    '/api/applications/ars/rules/v1/ruleset/stackable-savings',
    { params },
  ));
  const getAttachmentByApplicationIdAsync = applicationId => retryCallIfNeeded(client, () => client
    .get(`${baseEndpoint}/applications/${applicationId}/attachment`));
  const getApplicationsByIp = ipAddress => retryCallIfNeeded(client, () => client
    .get(`${baseEndpoint}/applications?index=additionalAttributes.ipAddress&value=${ipAddress}&limit=300`));

  // eslint-disable-next-line max-len
  const getEncodedApplicationByApplicationIdAsync = applicationId => retryCallIfNeeded(client, () => client.get(`${applicationProxy}/${applicationId}`));
  const postEncodedApplicationAsync = payload => retryCallIfNeeded(client, () => client
    .post(`${applicationProxy}`, payload));
  const updateEncodedApplicationAsync = async payload => client.put(`${applicationProxy}/${payload.i}`, payload);

  const aggregatedRuleCheckAsync = payload => retryCallIfNeeded(client, () => client
    .post(`${ruleEngineProxy}`, payload));

  const getDVRejectRuleAsync = payload => retryCallIfNeeded(client, () => client
    .post(`${dvRejectRuleProxy}`, payload));

  const getEncodedNavigationByApplicationIdAsync = applicationId => retryCallIfNeeded(client, () => client
    .get(`${navigationProxy}/${applicationId}`));
  const postEncodedNavigationAsync = payload => retryCallIfNeeded(client, () => client
    .post(`${navigationProxy}/${payload.i}`, payload));
  const updateEncodedNavigationAsync = async payload => client.put(`${navigationProxy}/${payload.i}`, payload);

  const isSSNNeverUsed = payload => retryCallIfNeeded(client, () => client.post(`${baseRulesEndpoint}/stateless/ssnValidator/isSSNNeverUsed`, payload), 0);
  return {
    getApplicationByPersonIdAsync,
    postApplicationAsync,
    getApplicationByApplicationIdAsync,
    updateApplicationAsync,
    patchApplicationAsync,
    getNavigationByApplicationIdAsync,
    updateNavigationAsync,
    postNavigationAsync,
    getApplicationType,
    getApplicationTypeDetails,
    putApplywebAppEnrollmentStatusAsync,
    uploadDocuments,
    uploadTranscriptDocument,
    getProgramWorkExperienceRequirement,
    getStackableSavingsRuleset,
    getAttachmentByApplicationIdAsync,
    getApplicationsByIp,
    getEncodedApplicationByApplicationIdAsync,
    postEncodedApplicationAsync,
    updateEncodedApplicationAsync,
    getEncodedNavigationByApplicationIdAsync,
    postEncodedNavigationAsync,
    updateEncodedNavigationAsync,
    isSSNNeverUsed,
    aggregatedRuleCheckAsync,
    getDVRejectRuleAsync,
  };
}
